import { useEffect } from "react";
import { Link } from "react-router-dom";
import TopNav from "@/components/TopNav";
import WaitlistForm from "@/components/WaitlistForm";
import PhoneCarousel from "@/components/PhoneCarousel";
import { LOGO_URL, POD_CAROUSEL } from "@/lib/rooted";

export default function Landing() {
  useEffect(() => {
    if (!window.location.hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, []);

  return (
    <div data-testid="page-landing" className="bg-rooted">
      <TopNav />

      {/* 1 — Hero */}
      <section
        id="hero"
        data-testid="section-hero"
        className="relative min-h-[100svh] flex items-center justify-center px-6 md:px-10 overflow-hidden pt-24"
      >
        <span
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1100px] h-[1100px] rounded-full"
          style={{
            background:
              "radial-gradient(circle at center, rgba(57,151,26,0.22) 0%, rgba(57,151,26,0.08) 32%, rgba(0,0,0,0) 60%)",
            filter: "blur(20px)",
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <p data-testid="social-proof" className="verse-cite mb-10 md:mb-12 opacity-80">
            Followed by 60k+ across social media, and growing.
          </p>

          <div data-testid="hero-lockup" className="rise flex flex-col items-center gap-3 mb-8 md:mb-10">
            <img
              src={LOGO_URL}
              alt="Rooted"
              data-testid="hero-logo"
              className="h-14 w-14 md:h-16 md:w-16 object-contain drop-shadow-[0_0_40px_rgba(57,151,26,0.45)]"
            />
            <span className="font-display text-2xl md:text-3xl tracking-[-0.02em] text-rooted">Rooted</span>
          </div>

          <h1
            data-testid="hero-heading"
            className="rise rise-delay-1 font-display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1.02] tracking-[-0.025em] text-rooted"
          >
            Bible <span className="italic brand-green">Together.</span>
          </h1>

          <p
            data-testid="hero-subheading"
            className="rise rise-delay-2 mt-8 md:mt-10 font-body text-lg md:text-xl text-soft-rooted max-w-2xl mx-auto leading-[1.6]"
          >
            A faith app built around studying Scripture{" "}
            <strong className="font-semibold text-rooted underline underline-offset-[6px] decoration-[#39971a] decoration-2">
              together
            </strong>
            , in small groups called Pods.
          </p>

          <div className="rise rise-delay-3 mt-10 md:mt-12">
            <WaitlistForm testId="hero-waitlist" variant="large" />
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 verse-cite opacity-50">
          Coming Soon · In Development
        </div>
      </section>

      {/* 2 — Problem, one line */}
      <section id="problem" data-testid="section-problem" className="bg-rooted py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <p
            data-testid="problem-line"
            className="font-display text-2xl md:text-4xl leading-[1.35] tracking-[-0.015em] text-rooted"
          >
            Staying consistent in your faith is hard to do alone.
            <br />
            <span className="text-muted-rooted">Rooted was built to fix that.</span>
          </p>
        </div>
      </section>

      {/* 3 — How it works (Pods, single pillar, no number) */}
      <section
        id="how-it-works"
        data-testid="section-how-it-works"
        className="bg-rooted section-y"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-6 flex justify-center">
              <div className="relative">
                <span
                  aria-hidden
                  className="pointer-events-none absolute -inset-10 rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle at center, rgba(57,151,26,0.18) 0%, rgba(57,151,26,0.05) 45%, rgba(0,0,0,0) 70%)",
                  }}
                />
                <PhoneCarousel
                  testId="pod-carousel"
                  label={POD_CAROUSEL.label}
                  steps={POD_CAROUSEL.steps}
                />
              </div>
            </div>
            <div className="lg:col-span-6">
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] text-rooted mb-8">
                How it <span className="italic brand-green">works.</span>
              </h2>
              <div className="space-y-5 font-body text-lg md:text-xl leading-[1.75] text-muted-rooted">
                <p>
                  Bible studies often fade after a few weeks because nobody is really holding the
                  group to it.
                </p>
                <p>
                  In a Pod, your group reads the same passage together, highlighting, scribbling
                  notes, and leaving voice memos on the very same verses, so you stay accountable
                  to each other and walk away with insight you would not have found alone.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 — Final CTA */}
      <section id="waitlist" data-testid="section-waitlist" className="bg-rooted section-y relative">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center relative">
          <span
            aria-hidden
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-20 w-[700px] h-[400px] rounded-full -z-0"
            style={{
              background:
                "radial-gradient(circle at center, rgba(57,151,26,0.14) 0%, rgba(57,151,26,0.04) 45%, rgba(0,0,0,0) 75%)",
            }}
          />
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-[-0.02em] text-rooted mb-6 relative">
            Rooted is still being built.
            <br />
            <span className="text-muted-rooted">Be part of it from the beginning.</span>
          </h2>
          <div className="mt-10 relative">
            <WaitlistForm testId="final-waitlist" variant="large" />
          </div>
        </div>
      </section>

      {/* 5 — Learn More link */}
      <section data-testid="section-learn-more" className="bg-rooted py-14">
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-center">
          <p className="font-body text-base md:text-lg text-muted-rooted">
            Curious what else Rooted offers?{" "}
            <Link
              to="/features"
              data-testid="learn-more-link"
              className="text-rooted hover:text-[#39971a] underline underline-offset-4 decoration-[#39971a]/40 hover:decoration-[#39971a] transition-colors"
            >
              Learn More
            </Link>
          </p>
        </div>
      </section>

      {/* 6 — Footer */}
      <footer data-testid="section-footer" className="bg-rooted">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Rooted" className="h-9 w-9" />
            <div>
              <div className="font-display text-lg text-rooted">Rooted</div>
              <div className="verse-cite mt-1">Bible Together.</div>
            </div>
          </div>
          <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 font-display text-sm text-muted-rooted">
            <Link to="/privacy" className="hover:text-[#39971a]">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#39971a]">Terms of Use</Link>
            <span className="text-muted-rooted/40">·</span>
            <span>Built by BeyondBible</span>
          </nav>
        </div>
        <div className="border-t border-[#f2ede6]/5">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 verse-cite flex justify-between">
            <span>© {new Date().getFullYear()} Rooted</span>
            <span className="hidden md:inline">Coming Soon · In Development</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
